// 24 小時制的日期時間欄位（SPEC UX-12 時間一律 24 小時制；VMS UX 評審第二輪 R2-P2-1、B2-30）：
// 原生 datetime-local 照瀏覽器語系顯示，繁中會變成「下午10:25」。這裡用一個文字欄位寫「2026-10-13 22:25」（打字也收「T」分隔），
// 旁邊一顆原生日期選擇器只換日期。對外的值跟 datetime-local 一樣是「YYYY-MM-DDTHH:mm」（本機時間），呼叫端不用改。
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** 「2026-10-13 22:25」「2026-10-13T22:25」「2026-10-13 9:05」→「2026-10-13T22:25」；不合法 → null。 */
export function parseDateTime24(text: string): string | null {
  const m = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})[T\s]+(\d{1,2}):(\d{2})(?::\d{2})?\s*$/.exec(text);
  if (!m) return null;
  const [y, mo, d, h, mi] = m.slice(1).map(Number);
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || h > 23 || mi > 59) return null;
  const dt = new Date(y, mo - 1, d, h, mi);
  if (dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null; // 2 月 30 日之類
  const p = (n: number) => String(n).padStart(2, '0');
  return `${y}-${p(mo)}-${p(d)}T${p(h)}:${p(mi)}`;
}

/** 「YYYY-MM-DDTHH:mm」→ 畫面上的「YYYY-MM-DD HH:mm」。 */
export function showDateTime24(v: string): string {
  return v ? v.replace('T', ' ').slice(0, 16) : '';
}

export function DateTimeInput({
  value,
  onChange,
  max,
  className,
  style,
  testId,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange(v: string): void;
  /** 「YYYY-MM-DDTHH:mm」；超過的不接受 */
  max?: string;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  'aria-label'?: string;
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(showDateTime24(value));
  const [bad, setBad] = useState(false);
  const errId = useId();
  // 外面改了值（網址、按鈕）就跟著換；自己正在打、還沒打完的不蓋掉
  useEffect(() => {
    if (parseDateTime24(text) !== value) {
      setText(showDateTime24(value));
      setBad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = (raw: string) => {
    setText(raw);
    if (raw.trim() === '') {
      setBad(false);
      onChange('');
      return;
    }
    const v = parseDateTime24(raw);
    const ok = !!v && (!max || v <= max);
    setBad(!ok && raw.trim().length >= 15);
    if (ok) onChange(v!);
  };
  const datePart = (parseDateTime24(text) ?? value).slice(0, 10);
  const timePart = (parseDateTime24(text) ?? value).slice(11, 16) || '00:00';
  return (
    <span className="dt24" style={style}>
      <input
        className={className ?? 'input'}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        placeholder="YYYY-MM-DD HH:mm"
        value={text}
        max={max}
        aria-label={ariaLabel}
        aria-invalid={bad || undefined}
        aria-describedby={bad ? errId : undefined}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => !bad && parseDateTime24(text) && setText(showDateTime24(parseDateTime24(text)!))}
        data-testid={testId}
        data-dt24
      />
      <input
        className="dt24-date"
        type="date"
        tabIndex={-1}
        value={datePart}
        max={max?.slice(0, 10)}
        aria-label={t('common.pick_date')}
        title={t('common.pick_date')}
        onChange={(e) => e.target.value && commit(`${e.target.value} ${timePart}`)}
      />
      {bad && (
        <span id={errId} className="error small">
          {t('common.dt24_bad')}
        </span>
      )}
    </span>
  );
}
